import { FLOW_MODES } from '@avanta/shared/constants/appointments';
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { AppointmentsReservePayload } from 'pages/AppointmentsReserve/constants';
import { type AppointmentsReserveFormData } from 'pages/AppointmentsReserve/types';
import { setSelectedCenter } from 'pages/AppointmentsReserve/utils';
import { useFetchInsuredCenters } from './useFetchInsuredCenters';

/**
 * Copies the speciality, treatment, duration and default center resolved from the server
 * into the reserve form. Downstream queries and the summary read them from the form.
 * Mount it once, in the component that hosts every step.
 */
export const useSyncSessionDefaults = (): void => {
  const { control, setValue, getValues } = useFormContext<AppointmentsReserveFormData>();
  const flow = useWatch({ control, name: AppointmentsReservePayload.flow });
  const { values, isLoading, centersOptions } = useFetchInsuredCenters();

  const isEditFlow = flow === FLOW_MODES.edit;
  const isSearchFacultativeFlow = flow === FLOW_MODES.search_facultative;
  const specialityId = values.speciality?.id;
  const treatmentId = values.treatment?.id;
  const durationInMinutes = values.treatment?.durationInMinutes ?? 0;
  const suggestedCenterId = values.center ? String(values.center.id) : '';
  const suggestedCenterCity = values.center?.city ?? '';

  useEffect(() => {
    if (isEditFlow || isLoading) return;

    setValue(AppointmentsReservePayload.specialityBooked, specialityId);
    setValue(AppointmentsReservePayload.psychologistServiceBooked, treatmentId);
    setValue(AppointmentsReservePayload.durationType, durationInMinutes);
  }, [isEditFlow, isLoading, specialityId, treatmentId, durationInMinutes, setValue]);

  useEffect(() => {
    if (isEditFlow || isLoading) return;

    const selectedCenterId = getValues(AppointmentsReservePayload.backofficeCenterId);
    if (centersOptions.some(center => center.value === selectedCenterId)) return;

    // With a single option, or a professional already chosen, there is nothing left to pick.
    const shouldAutoSelectCenter = centersOptions.length === 1 || isSearchFacultativeFlow;
    setSelectedCenter(
      setValue,
      shouldAutoSelectCenter ? suggestedCenterId : '',
      shouldAutoSelectCenter ? suggestedCenterCity : ''
    );
  }, [
    isEditFlow,
    isSearchFacultativeFlow,
    isLoading,
    centersOptions,
    suggestedCenterId,
    suggestedCenterCity,
    getValues,
    setValue,
  ]);
};
